import { useState, useEffect } from 'react';
import type { Podcast, Episode } from '@/types';
import { getEpisodes } from '@/lib/api';
import { useFavorites } from '@/context/FavoritesContext';
import { EpisodeList } from './EpisodeList';

interface PodcastDetailProps {
  podcast: Podcast;
  onBack: () => void;
}

export function PodcastDetail({ podcast, onBack }: PodcastDetailProps) {
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorite = isFavorite(podcast.id);

  useEffect(() => {
    async function loadEpisodes() {
      setIsLoading(true);
      setError(null);
      try {
        const response = await getEpisodes(podcast.id);
        if (response.success) {
          setEpisodes(response.items);
        } else {
          setError(response.message || 'Failed to load episodes');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load episodes');
      } finally {
        setIsLoading(false);
      }
    }

    loadEpisodes();
  }, [podcast.id]);

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-4"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>

        <div className="glass-panel">
          <div className="flex flex-col sm:flex-row gap-6">
            {/* Podcast artwork */}
            <div className="flex-shrink-0 w-full sm:w-48">
              <div className="aspect-square rounded-lg overflow-hidden bg-surface-50">
                {podcast.artwork || podcast.image ? (
                  <img
                    src={podcast.artwork || podcast.image}
                    alt={podcast.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/20">
                    <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                  </div>
                )}
              </div>
            </div>

            {/* Podcast info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-white mb-2">
                {podcast.title}
              </h1>
              <p className="text-white/60 mb-3">
                {podcast.author || podcast.ownerName}
              </p>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <button
                  onClick={() => toggleFavorite(podcast)}
                  className={`btn ${favorite ? 'btn-primary' : 'btn-secondary'}`}
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill={favorite ? 'currentColor' : 'none'}
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                    />
                  </svg>
                  {favorite ? 'Saved' : 'Save'}
                </button>

                {podcast.episodeCount > 0 && (
                  <span className="text-sm text-white/50">
                    {podcast.episodeCount} episodes
                  </span>
                )}

                {podcast.language && (
                  <span className="text-sm text-white/50 uppercase">
                    {podcast.language}
                  </span>
                )}
              </div>

              {podcast.description && (
                <p
                  className="text-sm text-white/70 line-clamp-4"
                  dangerouslySetInnerHTML={{
                    __html: podcast.description.replace(/<[^>]*>/g, ''),
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Episodes */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4">Episodes</h2>
        {error ? (
          <div className="glass-panel text-center text-red-400 py-8">
            <p>{error}</p>
          </div>
        ) : (
          <EpisodeList episodes={episodes} podcast={podcast} isLoading={isLoading} />
        )}
      </div>
    </div>
  );
}
